import { useCallback, useEffect, useRef } from "react"

export function useSimpleImageCache(urls: string[]) {
    const cache = useRef(new Map<string, ImageBitmap>())
    const inflight = useRef(new Map<string, Promise<ImageBitmap>>())
    const urlsRef = useRef(urls)
    urlsRef.current = urls

    useEffect(() => {
        const c = cache.current
        return () => {
            c.forEach((bitmap) => bitmap.close())
            c.clear()
        }
    }, [])

    const loadBitmap = useCallback(async (url: string) => {
        if (cache.current.has(url)) return cache.current.get(url)!
        if (inflight.current.has(url)) return inflight.current.get(url)!

        const promise = fetch(url, { credentials: "include" })
            .then((res) => res.blob())
            .then((blob) => createImageBitmap(blob))
            .then((bitmap) => {
                if (cache.current.size > 10) {
                    const [firstUrl, firstBitmap] = cache.current.entries().next().value!
                    firstBitmap.close()
                    cache.current.delete(firstUrl)
                }
                cache.current.set(url, bitmap)
                inflight.current.delete(url)
                return bitmap
            })

        inflight.current.set(url, promise)
        return promise
    }, [])

    const getImage = useCallback(
        async (index: number) => {
            const currentUrls = urlsRef.current
            const url = currentUrls[index]
            if (!url) return null

            try {
                const bitmap = await loadBitmap(url)

                if (bitmap) {
                    const radius = 3

                    for (let i = 1; i <= radius; i++) {
                        const nextUrl = currentUrls[index + i]
                        if (nextUrl) {
                            loadBitmap(nextUrl).catch(() => {})
                        }

                        const prevUrl = currentUrls[index - i]
                        if (prevUrl) {
                            loadBitmap(prevUrl).catch(() => {})
                        }
                    }
                }

                return bitmap
            } catch {
                return null
            }
        },
        [loadBitmap]
    )

    const hasImage = useCallback((index: number) => {
        const url = urlsRef.current[index]
        return !!url && cache.current.has(url)
    }, [])

    return { getImage, hasImage }
}
